import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { theme } from './styles/theme';
import { GlobalStyles } from './styles/GlobalStyles';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { API_BASE } from './services/api';

// Components
import UserTypeSelection from './components/auth/UserTypeSelection';
import AuthFlow from './components/auth/AuthFlow';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Dashboard from './components/dashboard/Dashboard';
import RutasModule from './components/rutas/RutasModule';
import Layout from './components/layout/Layout';
import ProveedoresModule from './components/proveedores/ProveedoresModule';
import LicitacionesModule from './components/licitaciones/LicitacionesModule';
import PreguntasModule from './components/preguntas/PreguntasModule';

// Componente para redirigir según tipo de usuario
const HomeRedirect: React.FC = () => {
  const { proveedor } = useAuth();
  const isAdmin = proveedor?.username === 'ADMINISTRADOR';
  
  if (isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Navigate to="/rutas" replace />;
};

// Ruta solo para Administradores
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { proveedor } = useAuth();
  const isAdmin = proveedor?.username === 'ADMINISTRADOR';
  
  if (!isAdmin) {
    return <Navigate to="/rutas" replace />;
  }
  
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, loginWithAzure } = useAuth();
  const [userType, setUserType] = useState<'externo' | 'interno' | null>(null);
  const [processingAzure, setProcessingAzure] = useState(false);

  // Procesar callback de Azure
  useEffect(() => {
    const processAzureCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code && !isAuthenticated && !processingAzure) {
        setProcessingAzure(true);
        
        try {
          // Intercambiar código por token y obtener info del usuario
          const response = await fetch(`${API_BASE}/api/auth/azure-callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, redirect_uri: window.location.origin })
          });
          
          const data = await response.json();
          
          if (response.ok && data.access_token) {
            // Guardar token y datos del usuario
            localStorage.setItem('auth_token', data.access_token);
            localStorage.setItem('auth_proveedor', JSON.stringify(data.proveedor));
            // Limpiar URL y recargar
            window.history.replaceState({}, document.title, '/');
            window.location.reload();
          } else {
            console.error('Error en callback Azure:', data.error);
            window.history.replaceState({}, document.title, '/');
          }
        } catch (error) {
          console.error('Error procesando callback Azure:', error);
          window.history.replaceState({}, document.title, '/');
        } finally {
          setProcessingAzure(false);
        }
      }
    };
    
    processAzureCallback();
  }, [isAuthenticated, processingAzure]);

  if (isLoading || processingAzure) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #008752 0%, #006B41 50%, #004D30 100%)',
        color: 'white',
        fontSize: '18px'
      }}>
        {processingAzure ? 'Procesando autenticación con Microsoft...' : 'Cargando...'}
      </div>
    );
  }

  if (!isAuthenticated) {
    if (!userType) {
      return <UserTypeSelection onSelectType={setUserType} />;
    }
    return <AuthFlow userType={userType} onBack={() => setUserType(null)} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<HomeRedirect />} />
        <Route path="dashboard" element={
          <ProtectedRoute>
            <AdminRoute>
              <Dashboard />
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="rutas" element={
          <ProtectedRoute>
            <RutasModule />
          </ProtectedRoute>
        } />
        <Route path="proveedores" element={
          <ProtectedRoute>
            <AdminRoute>
              <ProveedoresModule />
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="licitaciones" element={
          <ProtectedRoute>
            <AdminRoute>
              <LicitacionesModule />
            </AdminRoute>
          </ProtectedRoute>
        } />
        <Route path="preguntas" element={
          <ProtectedRoute>
            <PreguntasModule />
          </ProtectedRoute>
        } />
      </Route>
      
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;

